/**
 * Item actions replicating reware-admin's Inventory Items portal:
 * Accept / Reject / Review buttons (ItemRevisionButtons.jsx state rules) and
 * the "..." menu (InventoryItemComponent.jsx menuList) — Edit Item Details,
 * Use Catalog Photography, Reprint Label, Reprint Hangtag Label, Reset
 * Catalog Image Photography, Show Item Price Details, Sync to Shopify.
 */
import { useState } from 'react';
import { useNotify, useRefresh } from 'react-admin';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Tooltip,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';

import { requirePartner } from '@src/providers/dataProvider/shared';

import { formatCents } from './format';
import {
  acceptItem,
  applyCatalogPhotography,
  printItemHangtagLabel,
  printItemLabel,
  rejectItem,
  reshootLikeNewPhotography,
  reviewItem,
  syncShopifyCatalogProduct,
  updateItem,
} from './mutations';
import type { ConstantOption, InventoryItemRecord } from './types';

// State gates ported verbatim from reware-admin ItemRevisionButtons.jsx
const CAN_ACCEPT_STATES = [
  'CATALOG_REVIEW', 'DELETED', 'FORSALE_REVIEW', 'NO_SHELF', 'PARTNER_REJECTED',
  'PHOTOGRAPHY_EDIT', 'PHOTOGRAPHY_RESHOOT', 'REVIEW_PHOTOGRAPHY',
];
const CAN_REJECT_STATES = [
  'DELETED', 'FORSALE_APPROVED', 'FORSALE_UNLISTED', 'FORSALE_CURATION',
  'FORSALE_REJECTED', 'FORSALE_REVIEW', 'NO_SHELF', 'PARTNER_REJECTED',
  'PHOTOGRAPHY_EDIT', 'PHOTOGRAPHY_PENDING', 'PHOTOGRAPHY_RESHOOT',
  'REVIEW_PHOTOGRAPHY', 'SENDBACK_PENDING',
];
const CAN_REVIEW_STATES = [
  'DELETED', 'FORSALE_APPROVED', 'FORSALE_UNLISTED', 'FORSALE_CURATION',
  'NO_SHELF', 'PARTNER_REJECTED', 'PHOTOGRAPHY_EDIT', 'PHOTOGRAPHY_PENDING',
  'PHOTOGRAPHY_RESHOOT', 'REVIEW_PHOTOGRAPHY',
];
// Edit gate ported from InventoryItemComponent.jsx blockingStates
const EDIT_BLOCKING_STATES = [
  'MAINLINE_RTS', 'MAINLINE_OPENBOX', 'FORSALE_UNLISTED', 'FORSALE_REJECTED',
];

const hasAllRequiredPhotos = (record: InventoryItemRecord): boolean =>
  (record.photoUploadPositions ?? []).every(({ position, required }) => {
    if (!required) return true;
    const images = record.imageUrls ?? {};
    if (['front', 'main'].includes(position)) {
      return !!images.front || !!images.main;
    }
    return !!images[position];
  });

interface ItemActionsProps {
  record: InventoryItemRecord;
  conditions: ConstantOption[];
  rejectReasons: ConstantOption[];
}

type DialogKind = null | 'reject' | 'edit' | 'priceDetails';

export const ItemActions = ({ record, conditions, rejectReasons }: ItemActionsProps) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const [busy, setBusy] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [dialog, setDialog] = useState<DialogKind>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectNote, setRejectNote] = useState('');
  const [editPrice, setEditPrice] = useState(((record.price ?? 0) / 100).toFixed(2));
  const [editCondition, setEditCondition] = useState(record.condition ?? '');
  const [editNotes, setEditNotes] = useState(record.notes ?? '');

  // On success: notify (using the action's returned message when it provides
  // one — e.g. the Shopify sync — otherwise `label`), close the dialog/menu, and
  // refresh. On failure: keep the dialog open so the user's typed input isn't
  // discarded and they can retry.
  const run = async (label: string, action: () => Promise<void | string>) => {
    setBusy(true);
    try {
      const result = await action();
      notify(typeof result === 'string' && result ? result : label, { type: 'success' });
      setDialog(null);
      setMenuAnchor(null);
      refresh();
    } catch (error) {
      notify(error instanceof Error ? error.message : `${label} failed`, { type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const itemCanBeFound = record.state !== 'MISSING' || !!record.order?.id;

  // Disabled-reason strings surface the gate that blocked the action, so
  // users aren't left guessing why a button is greyed out.
  const acceptReason = !CAN_ACCEPT_STATES.includes(record.state) ?
    'Not available in the item’s current state' :
    !hasAllRequiredPhotos(record) ?
      'Required photos are missing' :
      record.isUnsellable ?
        'Item is marked unsellable' :
        null;
  const acceptDisabled = busy || !!acceptReason;

  const rejectReasonText = itemCanBeFound && !CAN_REJECT_STATES.includes(record.state) ?
    'Not available in the item’s current state' :
    null;
  const rejectDisabled = busy || !!rejectReasonText;

  const reviewReason = itemCanBeFound && !CAN_REVIEW_STATES.includes(record.state) ?
    'Not available in the item’s current state' :
    itemCanBeFound && record.state === 'PHOTOGRAPHY_PENDING' && !hasAllRequiredPhotos(record) ?
      'Required photos are missing' :
      null;
  const reviewDisabled = busy || !!reviewReason;

  const editDisabled = busy || EDIT_BLOCKING_STATES.includes(record.state);

  // Menu gates ported from InventoryItemComponent.jsx menuList
  const useCatalogPhotoDisabled = busy || !record.canUseCatalogPhotography;
  const reshootDisabled = busy ||
    Object.keys(record.inventoryImageUrls ?? {}).length > 0 ||
    !(record.photoInheritanceConditionCodes ?? []).includes(record.condition ?? '') ||
    Object.keys(record.catalogImageUrls ?? {}).length === 0 ||
    record.state === 'FORSALE_APPROVED';
  const syncDisabled = busy || !record.catalogItem?.parent_sku;

  const handleSync = () => {
    // requirePartner() runs inside run() so a missing/invalid partner surfaces
    // as an error toast. Returning the API message makes it run()'s single
    // success toast instead of a second, redundant one.
    void run('Sync to Shopify queued', () =>
      syncShopifyCatalogProduct(requirePartner().code, record.catalogItem?.parent_sku ?? ''));
  };

  const priceRows: Array<[string, string]> = [
    ['Item Price', formatCents(record.price)],
    ['Original Price', formatCents(record.catalogItem?.originalPrice)],
    ['Used Price', formatCents(record.catalogItem?.usedPrice)],
    ['Last Known Retail Price', formatCents(record.catalogItem?.lastKnownRetailPrice)],
    ['Last Known Outlet Price', formatCents(record.catalogItem?.lastKnownOutletPrice)],
    ['MSRP Price', formatCents(record.catalogItem?.msrpPrice)],
    ['Yerdle Price', formatCents(record.catalogItem?.yerdlePrice)],
    ['Y-Adjusted Price', formatCents(record.catalogItem?.yadjustedPrice)],
    ['Y-Adjusted Code', record.catalogItem?.yadjustedCode ?? '—'],
  ];

  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
      <Tooltip title={acceptReason ?? ''}>
        <span>
          <Button
            size="small"
            variant="contained"
            color="success"
            disabled={acceptDisabled}
            onClick={() => run('Item accepted', () => acceptItem(record.id))}
          >
            Accept
          </Button>
        </span>
      </Tooltip>
      <Tooltip title={rejectReasonText ?? ''}>
        <span>
          <Button
            size="small"
            variant="outlined"
            color="error"
            disabled={rejectDisabled}
            onClick={() => setDialog('reject')}
          >
            Reject
          </Button>
        </span>
      </Tooltip>
      <Tooltip title={reviewReason ?? ''}>
        <span>
          <Button
            size="small"
            variant="outlined"
            disabled={reviewDisabled}
            onClick={() => run('Item sent to review', () => reviewItem(record.id))}
          >
            Review
          </Button>
        </span>
      </Tooltip>
      <IconButton
        size="small"
        onClick={event => setMenuAnchor(event.currentTarget)}
        aria-label="More actions"
        sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>

      <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}>
        <MenuItem disabled={editDisabled} onClick={() => { setMenuAnchor(null); setDialog('edit'); }}>
          Edit Item Details
        </MenuItem>
        <MenuItem
          disabled={useCatalogPhotoDisabled}
          onClick={() => run('Catalog photography applied', () => applyCatalogPhotography(record.id))}
        >
          Use Catalog Photography
        </MenuItem>
        <MenuItem disabled={busy} onClick={() => run('Label sent to printer', () => printItemLabel(record.id))}>
          Reprint Label
        </MenuItem>
        <MenuItem
          disabled={busy}
          onClick={() => run('Hangtag label sent to printer', () => printItemHangtagLabel(record.id))}
        >
          Reprint Hangtag Label
        </MenuItem>
        <MenuItem
          disabled={reshootDisabled}
          onClick={() => run('Photography reset queued', () => reshootLikeNewPhotography(record.id))}
        >
          Reset Catalog Image Photography
        </MenuItem>
        <MenuItem onClick={() => { setMenuAnchor(null); setDialog('priceDetails'); }}>
          Show Item Price Details
        </MenuItem>
        <MenuItem disabled={syncDisabled} onClick={handleSync}>
          Sync to Shopify
        </MenuItem>
      </Menu>

      {/* Reject dialog (reason + optional note, like the admin portal) */}
      <Dialog open={dialog === 'reject'} onClose={() => setDialog(null)} fullWidth maxWidth="xs">
        <DialogTitle>
          Reject item #
          {record.id}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              select
              label="Reason"
              value={rejectReason}
              onChange={event => setRejectReason(event.target.value)}
              SelectProps={{ native: true }}
              InputLabelProps={{ shrink: true }}
            >
              <option value="" disabled>Select a reason</option>
              {rejectReasons.map(({ code, displayName }) => (
                <option key={code} value={code}>{displayName}</option>
              ))}
            </TextField>
            <TextField
              label="Note (optional)"
              value={rejectNote}
              onChange={event => setRejectNote(event.target.value)}
              multiline
              minRows={2}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            disabled={!rejectReason || busy}
            onClick={() => run('Item rejected', () => rejectItem(record.id, rejectReason, rejectNote || undefined))}
          >
            Reject
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit item details (price / condition / notes via updateInventoryItem) */}
      <Dialog open={dialog === 'edit'} onClose={() => setDialog(null)} fullWidth maxWidth="xs">
        <DialogTitle>
          Edit item #
          {record.id}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Price (USD)"
              value={editPrice}
              onChange={event => setEditPrice(event.target.value)}
              inputProps={{ inputMode: 'decimal' }}
            />
            <TextField
              select
              label="Condition"
              value={editCondition}
              onChange={event => setEditCondition(event.target.value)}
              SelectProps={{ native: true }}
              InputLabelProps={{ shrink: true }}
            >
              {conditions.map(({ code, displayName }) => (
                <option key={code} value={code}>{displayName}</option>
              ))}
            </TextField>
            <TextField
              label="Notes"
              value={editNotes}
              onChange={event => setEditNotes(event.target.value)}
              multiline
              minRows={3}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={busy || Number.isNaN(parseFloat(editPrice))}
            onClick={() => run('Item updated', () => updateItem({
              id: record.id,
              price: parseFloat(editPrice),
              condition: editCondition,
              notes: editNotes,
            }))}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Item price details (ItemPriceDetailDialog.jsx parity) */}
      <Dialog open={dialog === 'priceDetails'} onClose={() => setDialog(null)} fullWidth maxWidth="xs">
        <DialogTitle>Item Price Details</DialogTitle>
        <DialogContent>
          <Table size="small">
            <TableBody>
              {priceRows.map(([label, value]) => (
                <TableRow key={label}>
                  <TableCell component="th" sx={{ fontWeight: 600 }}>{label}</TableCell>
                  <TableCell align="right">{value}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default ItemActions;
